// Awareness utilities for tracking connected clients

import { wsProvider } from './connection'

export type Fruit = {
  emoji: string;
  name: string;
}

export const fruitList: Fruit[] = [
  { emoji: '🍇', name: 'Grapes' },
  { emoji: '🍈', name: 'Melon' },
  { emoji: '🍉', name: 'Watermelon' },
  { emoji: '🍊', name: 'Tangerine' },
  { emoji: '🍋', name: 'Lemon' },
  { emoji: '🍌', name: 'Banana' },
  { emoji: '🍍', name: 'Pineapple' },
  { emoji: '🥭', name: 'Mango' },
  //{ emoji: '🍎', name: 'Red Apple' },   //Only for the SERVER!
  { emoji: '🍏', name: 'Green Apple' },
  { emoji: '🍐', name: 'Pear' },
  { emoji: '🍑', name: 'Peach' },
]

export function getClientFruit(): { emoji: string; name: string } | null {
  if (wsProvider && wsProvider.awareness) {
    const localState = wsProvider.awareness.getLocalState()
    if (localState && localState.fruit && localState.emoji) {
      return { emoji: localState.emoji, name: localState.fruit }
    }
  }
  return null
}

export function getAllClients() {
  if (!wsProvider?.awareness) return []

  const states = Array.from(wsProvider.awareness.getStates().entries())
  const clientID = wsProvider.awareness.clientID
  return states.map(([clientId, state]: [number, any]) => ({
    id: clientId,
    fruit: state?.fruit || 'Unknown',
    emoji: state?.emoji || '❓',
    name: state?.name || 'Client',
    ip: state?.ip || 'Unknown',
    heartbeat: state?.heartbeat || 0,
    currentMenu: state?.currentMenu || null,
    wantedNavigation: state?.wantedNavigation || null,
    isLocal: clientId === clientID
  }))
}

export function updateCurrentMenu(menu: string | string[]) {
  if (!wsProvider?.awareness) return

  const path = Array.isArray(menu) ? menu.join('/') : menu
  const localState = wsProvider.awareness.getLocalState() || {}
  wsProvider.awareness.setLocalState({
    ...localState,
    currentMenu: path,
    heartbeat: Date.now()
  })
}

export function updateHighlightedTodo(todoIndex: number | null) {
  if (!wsProvider?.awareness) return

  const localState = wsProvider.awareness.getLocalState() || {}
  wsProvider.awareness.setLocalState({
    ...localState,
    highlightedTodo: todoIndex,
    heartbeat: Date.now()
  })
}

export function updateWantedNavigation(wantedNavigation: string | null) {
  if (!wsProvider?.awareness) return

  const localState = wsProvider.awareness.getLocalState() || {}
  wsProvider.awareness.setLocalState({
    ...localState,
    wantedNavigation,
    heartbeat: Date.now()
  })
}

export function sendNavigationCommand(targetClientId: number, navigationPath: string) {
  if (!wsProvider?.awareness) return

  const localState = wsProvider.awareness.getLocalState() || {}
  const navigationCommand = {
    targetClientId,
    navigationPath,
    timestamp: Date.now()
  }

  wsProvider.awareness.setLocalState({
    ...localState,
    navigationCommand,
    heartbeat: Date.now()
  })

  // Auto-clear the command after 5 seconds to prevent pollution
  setTimeout(() => {
    clearNavigationCommand();
  }, 5000);
}

export function clearNavigationCommand() {
  if (!wsProvider?.awareness) return

  const localState = wsProvider.awareness.getLocalState() || {}
  const { navigationCommand, ...stateWithoutCommand } = localState;

  wsProvider.awareness.setLocalState({
    ...stateWithoutCommand,
    heartbeat: Date.now()
  })
}

// Track processed commands to avoid reprocessing
const processedCommands = new Set<string>();

export function checkForNavigationCommands(): string | null {
  if (!wsProvider?.awareness) return null

  const myClientId = wsProvider.awareness.clientID
  const states = Array.from(wsProvider.awareness.getStates().entries())

  for (const [clientId, state] of states) {
    if (clientId === myClientId) continue // Skip my own state

    const command = state?.navigationCommand
    if (command &&
        command.targetClientId === myClientId &&
        command.timestamp &&
        Date.now() - command.timestamp < 30000) { // Command is less than 30 seconds old

      // Create unique command ID
      const commandId = `${clientId}-${command.timestamp}-${command.navigationPath}`;

      // Skip if already processed
      if (processedCommands.has(commandId)) {
        continue;
      }

      // Mark as processed
      processedCommands.add(commandId);

      // Clean up old processed commands (older than 1 minute)
      const oneMinuteAgo = Date.now() - 60000;
      for (const id of processedCommands) {
        const timestamp = parseInt(id.split('-')[1]);
        if (timestamp < oneMinuteAgo) {
          processedCommands.delete(id);
        }
      }

      return command.navigationPath;
    }
  }

  return null;
}

export function clearMyWantedNavigation() {
  if (!wsProvider?.awareness) return

  const localState = wsProvider.awareness.getLocalState() || {}
  wsProvider.awareness.setLocalState({
    ...localState,
    wantedNavigation: null,
    heartbeat: Date.now()
  })
}

export function getClientsHighlightingTodo(todoIndex: number) {
  if (!wsProvider?.awareness) return []
  
  const states = Array.from(wsProvider.awareness.getStates().entries())
  const clientID = wsProvider.awareness.clientID
  return states
    .filter(([clientId, state]: [number, any]) => 
      clientId !== clientID && // Exclude local client
      state && 
      state.highlightedTodo === todoIndex
    )
    .map(([, state]: [number, any]) => ({
      fruit: state?.fruit || 'Unknown',
      emoji: state?.emoji || '❓',
      name: state?.name || 'Client',
    }))
}

function getCachedIP(): string | undefined {
  try {
    const cacheKey = 'ipCache';
    const cachedDataString = typeof localStorage !== 'undefined' ? localStorage.getItem(cacheKey) : null;
    if (cachedDataString) {
      const cachedData = JSON.parse(cachedDataString);
      const fifteenMinutesInMs = 15 * 60 * 1000;
      const now = Date.now();
      if (now - cachedData.timestamp < fifteenMinutesInMs) {
        return cachedData.ip;
      }
    }
  } catch (e) {
    // ignore
  }
  return undefined;
}

export function setupAwareness({context, username}: {context: string, username?: string}) {

  console.log(`setupAwareness called from ${context} with username: ${username || 'N/A'}`)

  if (!wsProvider || !wsProvider.awareness) return;
  
  const usedFruits = new Set(
    Array.from(wsProvider.awareness.getStates().values())
      .map((state: any) => state && state.fruit)
  )
  
  const localState = wsProvider.awareness.getLocalState() || {}
  const ip = getCachedIP();
  
  if (!(localState.fruit && localState.emoji && !usedFruits.has(localState.fruit))) {
    const available = fruitList.filter(f => !usedFruits.has(f.name))
    const chosen = available.length > 0
      ? available[Math.floor(Math.random() * available.length)]
      : fruitList[Math.floor(Math.random() * fruitList.length)]
    
    wsProvider.awareness.setLocalState({
      fruit: `${chosen.name} - ${username || ''}`,
      emoji: chosen.emoji,
      name: 'Client',
      ...(ip ? { ip } : {}),
      heartbeat: Date.now()
    })
    console.log(`client awareness set on ${context}`)
  } else if (ip && localState.ip !== ip) {
    wsProvider.awareness.setLocalState({ ...localState, ip, heartbeat: Date.now() })
    console.log('client awareness updated with ip and heartbeat')
  } else {
    wsProvider.awareness.setLocalState({ ...localState, heartbeat: Date.now() })
    console.log('client awareness updated with heartbeat only')
  }
}

export function setupHeartbeat() {
  if (typeof window !== 'undefined') {
    if ((window as any)._clientHeartbeatInterval) {
      clearInterval((window as any)._clientHeartbeatInterval);
      console.log('cleared previous client heartbeat interval');
    }
    console.log('setting up client heartbeat interval');
    (window as any)._clientHeartbeatInterval = setInterval(() => {
      if (wsProvider && wsProvider.awareness) {
        const localState = wsProvider.awareness.getLocalState() || {}
        wsProvider.awareness.setLocalState({ ...localState, heartbeat: Date.now() })
        console.log('client heartbeat updated')
      }
    }, 23000)
  }
}
